import { useContext, useRoute } from '@nuxtjs/composition-api'
import { useVoucherHelpers } from './useVoucherHelpers'
import useTracking from '~/composable/tracking/useTracking'

/**
 * Composable for voucher-related tracking
 * Handles all tracking events for voucher application, removal, errors, and reapplication
 *
 * Separated from VoucherCode.vue to improve separation of concerns
 */
export const useVoucherTracking = (context) => {
  const appContext = context || useContext()
  const route = useRoute()
  const { track } = useTracking(appContext)
  const { productDetails, chosenChannel, chosenInstallment } = useVoucherHelpers()

  /**
   * Get category slug from route
   */
  const getCategorySlug = () => {
    return route.value?.params?.categoryslug?.toLowerCase()
  }

  /**
   * Track successful voucher application
   * @param {Object} params - Tracking parameters
   * @param {string} params.voucherCode - The voucher code that was applied
   * @param {Object} params.updatedCart - The updated cart object
   */
  const trackApplyVoucher = ({ voucherCode, updatedCart }) => {
    const categorySlug = getCategorySlug()
    const appliedCode = updatedCart?.appliedDiscountCodes?.[0]
    const discountAmount = updatedCart?.totalDiscountCodeDiscount
    const minimumPrice = appliedCode?.minimumPrice

    track('applyVoucher', {
      voucherCode,
      productDetails,
      chosenInstallment,
      chosenChannel,
      categorySlug,
      discountAmount,
      minimumPrice
    })
  }

  /**
   * Track voucher application error
   * @param {Object} params - Tracking parameters
   * @param {string} params.voucherCode - The voucher code that failed
   * @param {string} params.errorMessage - The error message
   * @param {string} params.errorCode - The error code (optional)
   */
  const trackApplyVoucherError = ({ voucherCode, errorMessage, errorCode = 'VOUCHER_ERROR' }) => {
    const categorySlug = getCategorySlug()
    track('applyVoucherError', {
      voucherCode,
      errorMessage,
      errorCode,
      productDetails,
      chosenInstallment,
      chosenChannel,
      categorySlug
    })
  }

  /**
   * Track voucher removal
   * @param {Object} params - Tracking parameters
   * @param {string} params.voucherCode - The voucher code that was removed
   */
  const trackRemoveVoucher = ({ voucherCode }) => {
    const categorySlug = getCategorySlug()
    track('removeVoucher', {
      voucherCode,
      productDetails,
      chosenInstallment,
      chosenChannel,
      categorySlug
    })
  }

  /**
   * Track voucher reapplication
   * @param {Object} params - Tracking parameters
   * @param {string} params.voucherCode - The voucher code being reapplied
   * @param {string} params.reason - The reason for reapplication (e.g., 'color change', 'channel change')
   */
  const trackReapplyVoucher = ({ voucherCode, reason }) => {
    const categorySlug = getCategorySlug()
    track('reapplyVoucher', {
      voucherCode,
      reason,
      productDetails,
      chosenInstallment,
      chosenChannel,
      categorySlug
    })
  }

  return {
    trackApplyVoucher,
    trackApplyVoucherError,
    trackRemoveVoucher,
    trackReapplyVoucher
  }
}
